import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import InternalNavbar from "../../components/feature/InternalNavbar";
import DashboardView, { SubData } from "./components/DashboardView";

const CLIENT_DATA_URL = "https://sdydqpjhqwlzaedmsncd.supabase.co/functions/v1/get-client-data";

type State = "idle" | "loading" | "not_found" | "error";

export default function CuentaPage() {
  const { i18n } = useTranslation();
  const isEn = i18n.language === "en";

  const [email, setEmail] = useState("");
  const [state, setState] = useState<State>("idle");
  const [subData, setSubData] = useState<SubData | null>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email.trim()) return;
    setState("loading");
    setSubData(null);
    try {
      const res = await fetch(CLIENT_DATA_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.toLowerCase().trim() }),
      });
      if (res.status === 404) {
        setState("not_found");
        return;
      }
      if (!res.ok) {
        setState("error");
        return;
      }
      const data: SubData = await res.json();
      setSubData(data);
      setState("idle");
    } catch {
      setState("error");
    }
  };

  const handleLogout = () => {
    setSubData(null);
    setEmail("");
    setState("idle");
  };

  if (subData) {
    return <DashboardView data={subData} isEn={isEn} onLogout={handleLogout} />;
  }

  return (
    <div className="min-h-screen bg-stone-50 flex flex-col">
      <InternalNavbar backTo={{ label: isEn ? "Back to home" : "Volver al inicio", href: "/" }} />

      <main className="flex-1 flex items-center justify-center px-6 py-20">
        <div className="w-full max-w-md">
          {/* Icon */}
          <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-amber-50 mx-auto mb-6">
            <i className="ri-user-settings-line text-2xl text-amber-600" />
          </div>

          <h1
            className="text-2xl font-bold text-stone-900 text-center mb-2"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {isEn ? "Access my account" : "Acceder a mi cuenta"}
          </h1>
          <p className="text-stone-500 text-sm text-center mb-8 leading-relaxed">
            {isEn
              ? "Enter the email you used to subscribe and we'll show you your personalised dashboard."
              : "Introduce el email con el que te suscribiste y te mostraremos tu panel personalizado."}
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label
                htmlFor="email"
                className="block text-xs font-semibold text-stone-600 mb-2 uppercase tracking-wide"
              >
                {isEn ? "Subscription email" : "Email de suscripción"}
              </label>
              <input
                id="email"
                type="email"
                name="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setState("idle");
                }}
                placeholder={isEn ? "your@email.com" : "tu@email.com"}
                required
                className="w-full border border-stone-200 rounded-xl px-4 py-3 text-sm text-stone-800 placeholder-stone-400 outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100 transition-all"
              />
            </div>

            {state === "not_found" && (
              <div className="flex items-start gap-3 bg-amber-50 border border-amber-100 rounded-xl px-4 py-3">
                <i className="ri-information-line text-amber-600 text-base flex-shrink-0 mt-0.5" />
                <p className="text-amber-700 text-sm">
                  {isEn
                    ? "No active subscription found for that email. Make sure it's the same email you used to complete the payment."
                    : "No encontramos ninguna suscripción activa con ese email. Comprueba que es el mismo con el que completaste el pago."}
                </p>
              </div>
            )}

            {state === "error" && (
              <div className="flex items-start gap-3 bg-red-50 border border-red-100 rounded-xl px-4 py-3">
                <i className="ri-error-warning-line text-red-500 text-base flex-shrink-0 mt-0.5" />
                <p className="text-red-600 text-sm">
                  {isEn
                    ? "Connection error. Please try again in a few seconds."
                    : "Error de conexión. Por favor, inténtalo de nuevo en unos segundos."}
                </p>
              </div>
            )}

            <button
              type="submit"
              disabled={state === "loading" || !email.trim()}
              className="w-full py-3.5 bg-amber-500 hover:bg-amber-400 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold text-sm rounded-xl transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
            >
              {state === "loading" ? (
                <>
                  <i className="ri-loader-4-line animate-spin" />
                  <span>{isEn ? "Looking up your account..." : "Buscando tu cuenta..."}</span>
                </>
              ) : (
                <>
                  <i className="ri-layout-grid-line text-xs" />
                  <span>{isEn ? "View my dashboard" : "Ver mi dashboard"}</span>
                </>
              )}
            </button>
          </form>

          {/* What they'll see */}
          <div className="mt-8 grid grid-cols-3 gap-3">
            {(isEn ? [
              { icon: "ri-bar-chart-2-line", text: "Plan & status" },
              { icon: "ri-settings-3-line", text: "Setup guide" },
              { icon: "ri-file-list-3-line", text: "Billing" },
            ] : [
              { icon: "ri-bar-chart-2-line", text: "Plan y estado" },
              { icon: "ri-settings-3-line", text: "Guía de configuración" },
              { icon: "ri-file-list-3-line", text: "Facturación" },
            ]).map((item) => (
              <div
                key={item.text}
                className="flex flex-col items-center gap-2 p-3 bg-white border border-stone-100 rounded-xl"
              >
                <div className="w-8 h-8 flex items-center justify-center">
                  <i className={`${item.icon} text-stone-400 text-lg`} />
                </div>
                <span className="text-stone-500 text-xs text-center">{item.text}</span>
              </div>
            ))}
          </div>

          <p className="text-center text-stone-400 text-xs mt-6">
            {isEn ? "Having trouble? " : "¿Problemas para acceder? "}
            <a
              href="mailto:soporteauria@gmail.com"
              className="text-amber-600 font-semibold hover:underline cursor-pointer"
            >
              {isEn ? "Contact support" : "Contacta con soporte"}
            </a>
          </p>
        </div>
      </main>

      <footer className="bg-white border-t border-stone-100 px-6 py-6">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-stone-400 text-xs">
            {isEn ? "© 2026 Auria. All rights reserved." : "© 2026 Auria. Todos los derechos reservados."}
          </p>
          <div className="flex items-center gap-4 text-xs">
            <Link to="/privacidad" className="text-stone-400 hover:text-stone-600 transition-colors cursor-pointer">
              {isEn ? "Privacy" : "Privacidad"}
            </Link>
            <Link to="/terminos" className="text-stone-400 hover:text-stone-600 transition-colors cursor-pointer">
              {isEn ? "Terms" : "Términos"}
            </Link>
            <a
              href="mailto:soporteauria@gmail.com"
              className="text-stone-400 hover:text-stone-600 transition-colors cursor-pointer"
            >
              {isEn ? "Support" : "Soporte"}
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
